/**
 * Tenant Configuration
 *
 * Baseline tenant configs. Site types that ship a bundle under `customize/`
 * supply their own config there and are merged in below, so this file never
 * names a customized tenant.
 *
 * Frontend counterpart to the backend's config/tenants.php.
 */
import { getTenantCustomizations } from '@customize/registry';

export interface TenantTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor?: string;
  textColor?: string;
  logo?: string;
  favicon?: string;
}

export interface TenantFeatures {
  [key: string]: boolean;
}

export interface TenantSettings {
  dateFormat: string;
  currency: string;
  language: string;
  [key: string]: unknown;
}

export interface ClinicNumberPolicy {
  maxLength: number;
  numericOnly: boolean;
  padLength?: number;
}

export interface InsurerDialogContactPolicy {
  phone: string;
  department: string;
  separateWarningContactLine?: boolean;
}

const DEFAULT_CLINIC_NUMBER_POLICY: ClinicNumberPolicy = {
  maxLength: 6,
  numericOnly: true,
  padLength: 6,
};

const DEFAULT_INSURER_DIALOG_CONTACT: InsurerDialogContactPolicy = {
  phone: '',
  department: 'システム管理者',
};

export interface TenantConfig {
  name: string;
  code: string;
  description: string;
  enabled: boolean;
  theme: TenantTheme;
  features: TenantFeatures;
  settings: TenantSettings;
}

export const DEFAULT_TENANT = 'default';

const baseTenantConfigs: Record<string, TenantConfig> = {
  default: {
    name: 'Default',
    code: 'default',
    description: 'Default tenant configuration',
    enabled: true,
    theme: {
      primaryColor: '#3b82f6',
      secondaryColor: '#10b981',
      backgroundColor: '#ffffff',
      textColor: '#1f2937',
    },
    features: {
      patients: true,
      consents: true,
      rezepts: true,
      visits: true,
    },
    settings: {
      dateFormat: 'YYYY/MM/DD',
      currency: '¥',
      language: 'ja',
      clinicNumber: DEFAULT_CLINIC_NUMBER_POLICY,
      insurerDialogContact: DEFAULT_INSURER_DIALOG_CONTACT,
    },
  },

  medix: {
    name: 'Medix',
    code: 'medix',
    description: 'Medix customized site',
    enabled: true,
    theme: {
      primaryColor: '#7c3aed',
      secondaryColor: '#a78bfa',
      backgroundColor: '#ffffff',
      textColor: '#1f2937',
      logo: '/assets/tenants/medix/logo.png',
    },
    features: {
      patients: true,
      consents: true,
      rezepts: true,
      visits: true,
    },
    settings: {
      dateFormat: 'YYYY/MM/DD',
      currency: '¥',
      language: 'ja',
      clinicNumber: DEFAULT_CLINIC_NUMBER_POLICY,
      insurerDialogContact: {
        phone: '03-3255-1095',
        department: '審査部',
      },
    },
  },

};

/**
 * Baseline configs plus every config contributed by `customize/`.
 *
 * Resolved lazily and memoised: `customize/` modules import back into `src/`,
 * and doing the merge at module-evaluation time would read the registry while
 * that import cycle is still in flight.
 */
let mergedConfigs: Record<string, TenantConfig> | null = null;

export const getTenantConfigs = (): Record<string, TenantConfig> => {
  if (!mergedConfigs) {
    mergedConfigs = getTenantCustomizations().reduce(
      (acc, tenant) => (tenant.config ? { ...acc, [tenant.code]: tenant.config } : acc),
      baseTenantConfigs
    );
  }
  return mergedConfigs;
};

/**
 * Get tenant configuration by code
 */
export const getTenantConfig = (tenantCode: string): TenantConfig => {
  const configs = getTenantConfigs();
  return configs[tenantCode] || configs[DEFAULT_TENANT];
};

/** Clinic identifier rules come from tenant metadata, never a tenant-name branch. */
export const getTenantClinicNumberPolicy = (tenantCode: string): ClinicNumberPolicy => {
  const candidate = getTenantConfig(tenantCode).settings.clinicNumber;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return DEFAULT_CLINIC_NUMBER_POLICY;
  }

  const value = candidate as Partial<ClinicNumberPolicy>;
  if (
    !Number.isSafeInteger(value.maxLength) ||
    (value.maxLength ?? 0) <= 0 ||
    typeof value.numericOnly !== 'boolean' ||
    (value.padLength !== undefined &&
      (!Number.isSafeInteger(value.padLength) || value.padLength <= 0))
  ) {
    return DEFAULT_CLINIC_NUMBER_POLICY;
  }

  return {
    maxLength: value.maxLength as number,
    numericOnly: value.numericOnly,
    ...(value.padLength === undefined ? {} : { padLength: value.padLength }),
  };
};

/** Contact copy for insurer lookup dialogs, resolved from tenant metadata. */
export const getTenantInsurerDialogContactPolicy = (
  tenantCode: string
): InsurerDialogContactPolicy => {
  const candidate = getTenantConfig(tenantCode).settings.insurerDialogContact;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return DEFAULT_INSURER_DIALOG_CONTACT;
  }

  const value = candidate as Partial<InsurerDialogContactPolicy>;
  if (typeof value.phone !== 'string' || typeof value.department !== 'string'
    || (value.separateWarningContactLine !== undefined
      && typeof value.separateWarningContactLine !== 'boolean')) {
    return DEFAULT_INSURER_DIALOG_CONTACT;
  }

  const department = value.department.trim();
  if (!department) return DEFAULT_INSURER_DIALOG_CONTACT;

  return {
    phone: value.phone.trim(),
    department,
    ...(value.separateWarningContactLine === undefined
      ? {} : { separateWarningContactLine: value.separateWarningContactLine }),
  };
};

/**
 * Check if tenant exists and is enabled
 */
export const isTenantEnabled = (tenantCode: string): boolean => {
  return getTenantConfigs()[tenantCode]?.enabled ?? false;
};

/**
 * Get all active tenants
 */
export const getActiveTenants = (): TenantConfig[] => {
  return Object.values(getTenantConfigs()).filter(config => config.enabled);
};
